import { Fragment } from 'react'
import PlanInput from './PlanInput'
import { formatRevenueCompact, formatRevenueFull } from './formatters'
import { type RequestComment } from './revisionRequest'
import RowNotes from './RowNotes'

interface Channel { id: string; name: string }
interface Quarter { label: string; start_date: string; end_date: string }

// One channel's row in the By Channel Quarterly matrix: past-quarter actuals
// (read-only), plan-quarter inputs (editable, growth-linked to the previous
// quarter), and the target-vs-sum column. Extracted from ByChannelQuarterly
// to keep that file under the line limit.
export default function QuarterlyMatrixRow({
  channel,
  pastQuarters,
  actualQuarters,
  planQuarters,
  past,
  actual,
  planValues,
  target,
  currency,
  onCellChange,
  notes,
}: {
  channel: Channel
  pastQuarters: Quarter[]
  actualQuarters: Quarter[]
  planQuarters: Quarter[]
  past: Record<string, string>
  actual: Record<string, string>
  planValues: Record<string, number>
  target: number
  currency?: string | null
  onCellChange: (quarterStart: string, next: number) => void
  notes?: RequestComment[]
}) {
  const sum = planQuarters.reduce((acc, q) => acc + (planValues[q.start_date] ?? 0), 0)
  const rowGap = target - sum
  const lastPastQ = pastQuarters[pastQuarters.length - 1]
  const pastQ4Value = lastPastQ ? Number(past[lastPastQ.start_date] ?? 0) : 0
  const lastActualQ = actualQuarters[actualQuarters.length - 1]
  const lastActualValue = lastActualQ ? Number(actual[lastActualQ.start_date] ?? 0) : 0
  const actualSum = actualQuarters.reduce((acc, q) => acc + Number(actual[q.start_date] ?? 0), 0)

  const delta = sum - target
  const balanced = target > 0 && delta === 0
  const over = delta > 0
  const deltaColor = target === 0
    ? 'var(--muted-foreground)'
    : balanced
      ? 'var(--success, var(--foreground))'
      : over
        ? 'var(--destructive)'
        : 'var(--muted-foreground)'
  let deltaLabel: string
  if (target === 0) {
    deltaLabel = '—'
  } else if (balanced) {
    deltaLabel = '✓ balanced'
  } else if (over) {
    deltaLabel = `${formatRevenueCompact(delta, currency)} over`
  } else {
    deltaLabel = `${formatRevenueCompact(Math.abs(delta), currency)} remaining`
  }

  return (
    <Fragment>
      <tr>
        <td
          className={`px-2 py-2 border-b border-border sticky left-0 bg-background ${notes?.length ? 'border-l-2 border-primary/40' : ''}`}
          style={{ color: 'var(--foreground)' }}
        >
          {channel.name}
        </td>
        {pastQuarters.map((q, i) => {
          const v = Number(past[q.start_date] ?? 0)
          return (
            <td
              key={`past-${channel.id}-${q.start_date}`}
              className="px-3 py-2 border-b border-border text-center tabular-nums"
              style={{
                color: 'var(--muted-foreground)',
                borderLeft: i === 0 ? '1px solid var(--border)' : undefined
              }}
            >
              {v > 0 ? formatRevenueCompact(v, currency) : '—'}
            </td>
          )
        })}
        {actualQuarters.map((q, i) => {
          const v = Number(actual[q.start_date] ?? 0)
          return (
            <td
              key={`actual-${channel.id}-${q.start_date}`}
              className="px-3 py-2 border-b border-border text-center tabular-nums"
              style={{
                color: 'var(--foreground)',
                borderLeft: i === 0 ? '2px solid var(--border)' : undefined
              }}
            >
              {v > 0 ? formatRevenueCompact(v, currency) : '—'}
            </td>
          )
        })}
        {planQuarters.map((q, i) => {
          const v = planValues[q.start_date] ?? 0
          const base = i === 0
            ? (actualQuarters.length > 0 ? lastActualValue : pastQ4Value)
            : Number(planValues[planQuarters[i - 1].start_date] ?? 0)
          return (
            <td
              key={`plan-${channel.id}-${q.start_date}`}
              colSpan={2}
              className="px-1 py-2 border-b border-border align-middle"
              style={{ borderLeft: i === 0 ? '2px solid var(--border)' : undefined }}
            >
              <PlanInput
                value={v}
                base={base}
                rowGap={rowGap}
                currency={currency}
                onCommit={(next) => onCellChange(q.start_date, next)}
              />
            </td>
          )
        })}
        <td
          className="px-2 py-2 border-b border-border text-right tabular-nums sticky right-0 bg-background"
          style={{ borderLeft: '2px solid var(--border)' }}
          title={target > 0 ? `Sum ${formatRevenueFull(sum, currency)} / Target ${formatRevenueFull(target, currency)}` : undefined}
        >
          <div style={{ color: 'var(--foreground)' }}>
            {target > 0 ? formatRevenueCompact(target, currency) : '—'}
          </div>
          <div className="text-[11px]" style={{ color: deltaColor }}>
            {deltaLabel}
          </div>
          {actualQuarters.length > 0 && (
            <div className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
              Full year {formatRevenueCompact(actualSum + sum, currency)}
            </div>
          )}
        </td>
      </tr>
      {notes && notes.length > 0 && (
        <tr>
          <td colSpan={2 + pastQuarters.length + actualQuarters.length + 2 * planQuarters.length} className="border-b border-border p-0">
            <RowNotes notes={notes} withTabBar />
          </td>
        </tr>
      )}
    </Fragment>
  )
}
